//@flow
/**
 * Created by Pawan on 06/03/2017.
 */

import ParamsUtil from '../util/ParamsUtil';
import HttpOperation from '../protocol/HttpOperation';
import Storage from '../protocol/Storage';
import ConnectionUtil from '../util/session/ConnectionUtil';
import VabLoginExecutor from './executors/VabLoginExecutor';
import System from '../basic/System';
import AuthMechanism, { AuthRealm } from '../auth/AuthMechanism';
import AuthenticationController from '../controllers/AuthenticationController';
import Authenticator from '../protocol/Authenticator';
import Authenticator2 from '../auth/Authenticator2';

const logger = require('../debug/debug')('wc:queries:LoginQuery');

export default class LoginQuery {
  username: string;
  password: string;

  /**
   * @private
   */
  passwordIsCookie: boolean;
  licenseId: ?string;
  authRealm: AuthRealm;

  constructor(username: string, password: string, passwordIsCookie: boolean = false) {
    this.username = username;
    this.password = password;
    this.passwordIsCookie = passwordIsCookie;
    this.authRealm = Authenticator.AUTHENTICATOR_INSTANCE.authRealm;
  }

  /**
   *
   * @private
   * @param {string} [cookie]
   * @param {string} [repoId]
   * @returns {FormAuthData}
   * @memberof LoginQuery
   */
  createFormAuthData(cookie?: string, repoId?: string): FormAuthData {
    const formAuthData: FormAuthData = {
      zm_username: this.username,
      zm_app_name: System.getAppName(),
      zm_cookie: cookie,
      zm_password: !!cookie === false ? this.password : undefined,
      zm_repo_id: repoId,
      realm: Authenticator2.realmName(this.authRealm),
      auth_mechanism: this.authRealm.authMechanism.toString()
    };
    formAuthData.extclient = true;
    formAuthData.thiscommandonly = true;
    formAuthData.browserurl = ConnectionUtil.getBaseUrl();
    formAuthData.zm_web_session = Storage.get('zm_web_session');
    return formAuthData;
  }

  submitAuthDataAsForm(): Promise<any> {
    const formData: FormAuthData = this.createFormAuthData(
      this.passwordIsCookie ? this.password : undefined,
      undefined
    );
    if (logger.enabled) {
      logger('[run()] Sending login query to /webclient/j_security_check. Data=> %o', formData);
    }
    const authMechanismString = this.authRealm.authMechanism.toString();
    return this.submitAsFormUrlEncoded(ConnectionUtil.getAppAuthURL(), formData);
  }

  /**
   * @param {String} url  URL to which data has to be submitted as form url encoded data
   * @param {*} data Object containing key value data.
   * @returns {Promise.<Object>} Returns a project, whose success is json object
   */
  submitAsFormUrlEncoded(url, data, headers: { [key: string]: string } = {}) {
    const formBody = data ? ParamsUtil.convertObjectToUrlEncodedFormData(data) : data;
    const allHeaders = formBody
      ? {
        'Content-Type': 'application/x-www-form-urlencoded',
        ...headers
      }
      : headers;

    return fetch(url, {
      method: formBody ? 'POST' : 'GET',
      mode: 'no-cors',
      cache: 'no-cache',
      credentials: 'include', //Include cookies for CORs request
      headers: allHeaders,
      body: formBody
    }).then((response) => {
      Authenticator2.setAuthTypesFromFetchHeaders(response.headers);
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.indexOf('application/json') !== -1) {
        return response.json();
      } else {
        return response.text();
      }
    });
  }


  static setErrorInWindowGsOnAuthFailure(errorMsg: string): void {
    logger('Login failed for user: ', errorMsg);
    window.setError(errorMsg);
  }

  handleResponse(data: any, resolve: (data: any) => void, reject: (data: any) => void): void {
    if (HttpOperation.checkForAuthFailure(200, data)) {
      LoginQuery.setErrorInWindowGsOnAuthFailure(data.errorMsg);
      if (System.isBrowserApp()) {
        Authenticator.getInstance().invokeOnAuthFailureCallback(null);
      }
      reject(data);
    } else {
      if (data.zm_username !== this.username) {
        logger(
          'Received username: %s not equal to entered %s. Storing them in map for future check.',
          data.zm_username,
          this.username
        );
      }
      logger('Login successful. Data: %o', data);
      resolve(data);
    }
  }

  getLoginPromise(): Promise<any> {
    return System.isBrowserApp()
      ? this.submitAuthDataAsForm()
      : new VabLoginExecutor(this.username, undefined, this.password).run();
  }

  run(): Promise<any> {
    return new Promise((resolve: (data: any) => void, reject: (data: any) => void) => {
      const promise: Promise<any> = this.getLoginPromise();
      promise
        .then((data) => {
          this.handleResponse(data, resolve, reject);
        })
        .catch(function(error) {
          reject(error);
        });
    });
  }
}

type FormAuthData = {
  zm_username: string,
  zm_password?: string,
  zm_cookie?: string,
  zm_repo_id?: string,
  thiscommandonly: boolean,
  extclient: boolean,
  browserurl: string,
  zm_app_name: string,
  zm_web_session: string,
  realm: string
};
